import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  Package, DollarSign, TrendingUp, TrendingDown, Receipt, XCircle,
  Activity, Trophy, Route, Sparkles, ArrowRight, Zap, CalendarClock,
} from "lucide-react";
import { formatDateLong, scheduleUrgency, shortOrderId } from "../../lib/format";
import { computeStageEvents, totalElapsedMs, fmtElapsedClock, elapsedTone } from "../../lib/stageTiming";

/** Tono visual de la etiqueta "Fecha programada" según urgencia. */
const SCHEDULE_TONE = {
  immediate: { wrap: "text-neutral-500",       icon: "text-neutral-400", value: "text-neutral-700" },
  upcoming:  { wrap: "text-blue-700",          icon: "text-blue-500",    value: "text-blue-900 font-semibold" },
  today:     { wrap: "text-amber-800",         icon: "text-amber-600",   value: "text-amber-900 font-semibold" },
  overdue:   { wrap: "text-red-700",           icon: "text-red-600",     value: "text-red-800 font-bold" },
};
import { useCompany } from "../../contexts/CompanyContext";

const STATUS_LABEL = {
  pending: "Recibido",
  confirmed: "Confirmado",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado",
};
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-violet-100 text-violet-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

/** Live elapsed-time counter — freezes automatically when delivered/cancelled
 *  (muestra el tiempo final transcurrido en lugar de seguir corriendo). */
function ElapsedSinceReceived({ order }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return; // frozen — no interval needed
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [closed]);
  const ms = totalElapsedMs(order, now);
  if (ms == null) return null;
  const clock = fmtElapsedClock(ms);
  const tone = elapsedTone(ms, { closed });
  return (
    <span
      className={`font-mono text-[11px] tabular-nums ${tone}`}
      title={closed ? "Tiempo total desde la recepción hasta el cierre" : "Tiempo desde la recepción"}
    >
      {clock}
    </span>
  );
}

/** Lista compacta de tiempos por status: chips redondeados. */
function StageAges({ order, className = "" }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [closed]);
  const events = computeStageEvents(order, now);
  if (events.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 text-[11px] ${className}`} data-testid={`stage-ages-${order?.order_id || ""}`}>
      {events.map((ev) => (
        <span
          key={ev.status + ev.at}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200"
        >
          <span className="font-semibold text-neutral-800">{ev.label}</span>
          <span className="text-neutral-500">hace {ev.ageLabel}</span>
        </span>
      ))}
    </div>
  );
}

/** Compact SVG sparkline. `data` = number[]. Auto-fits. */
function Sparkline({ data, color = "currentColor", height = 36 }) {
  if (!data || data.length === 0) return null;
  const w = 110;
  const h = height;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = w / (data.length - 1 || 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = h - ((v - min) / range) * (h - 6) - 3;
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${path} L${w},${h} L0,${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} className="overflow-visible">
      <path d={areaPath} fill={color} fillOpacity="0.10" />
      <path d={path} stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      {points.length > 0 && (
        <circle
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="2.6"
          fill={color}
        />
      )}
    </svg>
  );
}

/** Pulsing "live" dot. */
function LiveDot({ tone = "emerald" }) {
  const map = {
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    sky: "bg-sky-500",
  };
  return (
    <span className="relative inline-flex h-2 w-2 shrink-0">
      <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${map[tone]}`} />
      <span className={`relative inline-flex h-2 w-2 rounded-full ${map[tone]}`} />
    </span>
  );
}

/**
 * Hero KPI card. Estilo Linear/Stripe: borde con gradient sutil, hover
 * lift, label arriba con icono y live-dot, valor en display tabular,
 * delta y sparkline al pie.
 */
function HeroKPI({ icon: Icon, label, value, delta, deltaTone, sparkData, sparkColor, footer, live = true, testId }) {
  return (
    <div
      className="group relative rounded-2xl bg-white dark:bg-[#18181b] border border-neutral-200 dark:border-[#2d2d33] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(0,0,0,0.07)]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-neutral-500 uppercase tracking-wider">
          <Icon size={14} className="text-neutral-400" />
          <span>{label}</span>
        </div>
        {live && <LiveDot />}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <p className="font-display font-extrabold text-3xl tabular-nums leading-none">{value}</p>
        {delta && (
          <span className={`text-xs font-semibold inline-flex items-center gap-0.5 ${
            deltaTone === "up" ? "text-emerald-600"
              : deltaTone === "down" ? "text-red-600"
              : "text-neutral-500"
          }`}>
            {deltaTone === "up" ? <TrendingUp size={12} /> : deltaTone === "down" ? <TrendingDown size={12} /> : null}
            {delta}
          </span>
        )}
      </div>
      {footer && <p className="text-[11px] text-neutral-500 mt-1">{footer}</p>}
      {sparkData && (
        <div className="mt-3 -mb-1 text-neutral-400" style={{ color: sparkColor || "currentColor" }}>
          <Sparkline data={sparkData} color={sparkColor || "#0A0A0A"} />
        </div>
      )}
    </div>
  );
}

/** Embudo de pedidos hoy con barras y % conversión. Cada card es un link
    que filtra la lista de pedidos por ese estado y por "delivery hoy". */
function OrdersFunnel({ funnel }) {
  const { pending = 0, confirmed = 0, on_the_way = 0, delivered = 0 } = funnel || {};
  const stages = [
    { key: "pending", label: "Recibido", value: pending, color: "#F59E0B" },
    { key: "confirmed", label: "Confirmado", value: confirmed, color: "#3B82F6" },
    { key: "on_the_way", label: "En camino", value: on_the_way, color: "#8B5CF6" },
    { key: "delivered", label: "Entregado", value: delivered, color: "#10B981" },
  ];
  const max = Math.max(pending, confirmed, on_the_way, delivered, 1);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="orders-funnel">
      {stages.map((s, i) => {
        const pct = Math.round((s.value / max) * 100);
        const conv = i > 0 && stages[i - 1].value > 0
          ? Math.round((s.value / stages[i - 1].value) * 100)
          : null;
        return (
          <div key={s.key} className="relative">
            <Link
              to={`/admin/orders?status=${s.key}&day=today`}
              className="group block rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-neutral-300 tap-fade"
              data-testid={`funnel-card-${s.key}`}
            >
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-neutral-700">{s.label}</span>
                {conv !== null && (
                  <span className="text-neutral-400 font-mono tabular-nums">{conv}%</span>
                )}
              </div>
              <p className="font-display font-extrabold text-2xl tabular-nums mt-1">{s.value}</p>
              <div className="h-1.5 rounded-full bg-neutral-100 mt-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${pct}%`, backgroundColor: s.color }}
                />
              </div>
              <p className="text-[10px] text-neutral-400 mt-2 group-hover:text-neutral-600 transition-colors">
                Ver pedidos →
              </p>
            </Link>
            {i < stages.length - 1 && (
              <ArrowRight
                size={14}
                className="hidden sm:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 text-neutral-300"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminDashboard() {
  const { currentCompany } = useCompany();
  const tz = currentCompany?.timezone;
  const [d, setD] = useState(null);
  const [orders, setOrders] = useState([]);
  const [attribution, setAttribution] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    const load = () => {
      api.get("/metrics/dashboard").then((r) => setD(r.data)).catch(() => {});
      api.get("/orders/admin", { params: { page: 1, page_size: 5 } })
        .then((r) => setOrders(r.data?.items || []))
        .catch(() => {});
      api.get("/orders/admin/attribution")
        .then((r) => setAttribution(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
      api.get("/orders/admin/ai-insights")
        .then((r) => setAiInsights(r.data))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const sparkRevenue = useMemo(
    () => (d?.sparkline_7d || []).map((x) => Number(x.revenue || 0)),
    [d],
  );
  const sparkOrders = useMemo(
    () => (d?.sparkline_7d || []).map((x) => Number(x.orders || 0)),
    [d],
  );

  const deltaPct = d?.revenue_delta_pct;
  const deltaLabel = deltaPct === null || deltaPct === undefined
    ? null
    : `${deltaPct > 0 ? "+" : ""}${deltaPct}%`;
  const deltaTone = deltaPct === null || deltaPct === undefined
    ? "flat"
    : deltaPct > 0 ? "up" : deltaPct < 0 ? "down" : "flat";

  const cancelTone = (d?.cancellation_rate_pct ?? 0) >= 10
    ? "red" : (d?.cancellation_rate_pct ?? 0) >= 5 ? "amber" : "emerald";

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Panel de control</h1>
          <p className="text-neutral-500 text-sm mt-1 flex items-center gap-2">
            <LiveDot />
            <span>En vivo · actualiza cada 15s</span>
          </p>
        </div>
        {d?.new_today > 0 && (
          <Link
            to="/admin/orders?status=pending&day=today"
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-amber-600 transition"
            data-testid="dashboard-new-orders-badge"
          >
            <Zap size={14} />
            {d.new_today} nuevo{d.new_today === 1 ? "" : "s"} sin atender
          </Link>
        )}
      </div>

      {/* HERO KPI grid — 12 cols asimétrico */}
      <div className="mt-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-3">
          <HeroKPI
            icon={DollarSign}
            label="Ingresos hoy"
            value={d ? money(d.revenue_today) : "—"}
            delta={deltaLabel || undefined}
            deltaTone={deltaTone}
            sparkData={sparkRevenue}
            sparkColor="#10B981"
            footer={d ? `Ayer: ${money(d.revenue_yesterday)}` : "Cargando..."}
            testId="kpi-revenue-today"
          />
        </div>
        <div className="col-span-12 md:col-span-3">
          <HeroKPI
            icon={Receipt}
            label="Ticket promedio"
            value={d ? money(d.avg_ticket) : "—"}
            sparkData={sparkRevenue}
            sparkColor="#64748b"
            footer={d ? `${d.delivered_count} entregados` : "Cargando..."}
            testId="kpi-avg-ticket"
          />
        </div>
        <div className="col-span-12 md:col-span-3">
          <HeroKPI
            icon={XCircle}
            label="Cancelación"
            value={d ? `${d.cancellation_rate_pct}%` : "—"}
            sparkData={sparkOrders}
            sparkColor={cancelTone === "red" ? "#EF4444" : cancelTone === "amber" ? "#F59E0B" : "#10B981"}
            footer={d ? `${d.cancelled_lifetime} de ${d.total_lifetime}` : "Cargando..."}
            testId="kpi-cancellation"
            live={false}
          />
        </div>
        <div className="col-span-12 md:col-span-3">
          <HeroKPI
            icon={Activity}
            label="En curso"
            value={d?.in_progress ?? "—"}
            sparkData={sparkOrders}
            sparkColor="#8B5CF6"
            footer="Pendiente / Confirmado / En camino"
            testId="kpi-in-progress"
          />
        </div>
      </div>

      {/* Sub-row: Best product + Avg distance */}
      <div className="mt-4 grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-7 rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-medium text-neutral-500 uppercase tracking-wider">
            <Trophy size={14} className="text-amber-500" />
            <span>Mejor producto · Últimos 30 días</span>
          </div>
          {d?.best_product_30d ? (
            <div className="mt-3 flex items-end justify-between gap-3">
              <div className="min-w-0">
                <p className="font-display font-extrabold text-xl truncate">{d.best_product_30d.name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {d.best_product_30d.units} unidad{d.best_product_30d.units === 1 ? "" : "es"} vendidas
                </p>
              </div>
              <p className="font-display font-bold text-lg text-emerald-600 tabular-nums whitespace-nowrap">
                {money(d.best_product_30d.revenue)}
              </p>
            </div>
          ) : (
            <p className="text-sm text-neutral-400 mt-3">Sin entregas todavía este mes.</p>
          )}
        </div>
        <div className="col-span-12 md:col-span-5 rounded-2xl border border-neutral-200 bg-white p-5">
          <div className="flex items-center gap-2 text-xs font-medium text-neutral-500 uppercase tracking-wider">
            <Route size={14} className="text-sky-500" />
            <span>Distancia promedio</span>
          </div>
          <p className="font-display font-extrabold text-3xl tabular-nums mt-3">
            {d?.avg_distance_km != null ? `${d.avg_distance_km}` : "—"}
            <span className="text-sm font-medium text-neutral-400 ml-1">km</span>
          </p>
          <p className="text-[11px] text-neutral-500 mt-1">Promedio sobre pedidos entregados.</p>
        </div>
      </div>

      {/* Funnel */}
      <div className="mt-8">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Sparkles size={16} className="text-neutral-500" />
            Embudo de pedidos · Hoy
          </h2>
          <p className="text-xs text-neutral-500">
            Incluye pedidos creados hoy + pedidos programados con entrega para hoy.
          </p>
        </div>
        <OrdersFunnel funnel={d?.funnel_today} />
      </div>

      {/* Attribution: ¿de dónde vienen los pedidos? */}
      <AttributionCard rows={attribution} />

      {/* AI Insights */}
      <AIInsightsCard data={aiInsights} />

      {/* Recent orders */}
      <div className="mt-8 bg-white rounded-2xl border border-neutral-200 p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            Últimos pedidos
            {d?.new_today > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {d.new_today} nuevos
              </span>
            )}
          </h2>
          <Link to="/admin/orders" className="text-sm font-semibold underline" data-testid="see-all-orders">
            Ver todos
          </Link>
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          Tiempo desde la recepción del pedido (no se cuenta cuando ya fue entregado o cancelado).
        </p>
        <div className="mt-4 space-y-2">
          {orders.length === 0 && <p className="text-sm text-neutral-500">Aún no hay pedidos.</p>}
          {orders.map((o) => {
            return (
              <Link
                key={o.order_id}
                to={`/admin/orders/${o.order_id}`}
                className="block rounded-xl border border-neutral-100 hover:border-neutral-300 hover:bg-neutral-50 px-3 py-3 transition-colors tap-fade"
                data-testid={`dashboard-order-${o.order_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold truncate">{o.client_name || "Cliente"}</p>
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || "bg-neutral-100 text-neutral-600"}`}
                      >
                        {STATUS_LABEL[o.status] || o.status}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5 truncate">
                      {o.items?.length || 0} producto{(o.items?.length || 0) === 1 ? "" : "s"}
                      {" · "}
                      <span className="font-mono">#{shortOrderId(o.order_id)}</span>
                    </p>
                    <p
                      className={`text-[11px] mt-0.5 truncate flex items-center gap-1 ${SCHEDULE_TONE[scheduleUrgency(o, tz)].wrap}`}
                      data-testid={`dashboard-order-schedule-${o.order_id}`}
                    >
                      <CalendarClock size={11} className={`shrink-0 ${SCHEDULE_TONE[scheduleUrgency(o, tz)].icon}`} />
                      <span className="truncate">
                        Fecha programada:{" "}
                        <span className={SCHEDULE_TONE[scheduleUrgency(o, tz)].value}>
                          {o.when === "scheduled" && o.scheduled_at
                            ? formatDateLong(o.scheduled_at, tz)
                            : "Inmediata"}
                        </span>
                      </span>
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <ElapsedSinceReceived order={o} />
                    </div>
                    <StageAges order={o} className="mt-1" />
                  </div>
                  <p className="font-display font-bold whitespace-nowrap tabular-nums">{money(o.total)}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
}

/**
 * Tabla pequeña + barras horizontales con el reparto de pedidos por
 * canal de origen (source). Los labels y colores se asignan de forma
 * estable por source — si aparece un canal nuevo desde el backend
 * (futuras campañas), cae al gris neutral por defecto.
 */
const SOURCE_META = {
  "wa-bot":        { label: "Bot WhatsApp",        color: "bg-emerald-500", text: "text-emerald-700" },
  "wa-onboarding": { label: "WhatsApp · onboarding", color: "bg-emerald-400", text: "text-emerald-700" },
  "direct":        { label: "Directo · portal",    color: "bg-neutral-700", text: "text-neutral-700" },
  "qr":            { label: "QR físico",           color: "bg-amber-500",   text: "text-amber-700" },
  "ads":           { label: "Anuncios",            color: "bg-violet-500",  text: "text-violet-700" },
};

function AttributionCard({ rows = [] }) {
  const total = rows.reduce((acc, r) => acc + (r.count || 0), 0);
  return (
    <div className="mt-8 bg-white rounded-2xl border border-neutral-200 p-5" data-testid="attribution-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display font-bold text-xl flex items-center gap-2">
          ¿De dónde vienen los pedidos?
        </h2>
        <p className="text-xs text-neutral-500">{total} pedidos en total</p>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Reparto por canal de origen. Mide cuántos clientes que tocan el bot terminan en la app.
      </p>
      {rows.length === 0 ? (
        <p className="text-sm text-neutral-400 mt-4">Aún no hay datos de atribución.</p>
      ) : (
        <div className="mt-5 space-y-3">
          {rows.map((r) => {
            const meta = SOURCE_META[r.source] || { label: r.source, color: "bg-neutral-400", text: "text-neutral-600" };
            const pct = total > 0 ? Math.round((r.count / total) * 1000) / 10 : 0;
            return (
              <div key={r.source} data-testid={`attribution-row-${r.source}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className={`font-medium ${meta.text}`}>{meta.label}</span>
                  <span className="tabular-nums text-neutral-700">
                    <span className="font-semibold">{r.count}</span>
                    <span className="text-neutral-400"> · {pct}%</span>
                  </span>
                </div>
                <div className="h-2 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
                  <div className={`h-full ${meta.color} rounded-full`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AIInsightsCard({ data }) {
  if (!data) return null;
  const total = data.total_orders || 0;
  const ai = data.ai_handled || 0;
  const manual = data.manual_handled || 0;
  const aiPct = total > 0 ? Math.round((ai / total) * 1000) / 10 : 0;
  const fmtMin = (m) => (m == null ? "—" : `${m} min`);
  return (
    <div className="mt-8 bg-white rounded-2xl border border-neutral-200 p-5" data-testid="ai-insights-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display font-bold text-xl flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-indigo-600 text-white text-xs font-bold">AI</span>
          Insights del Auto-piloto
        </h2>
        <p className="text-xs text-neutral-500">{total} pedidos analizados</p>
      </div>
      <p className="text-xs text-neutral-500 mt-1">
        Pedidos resueltos automáticamente vs manualmente, cierres por geocerca y tiempos promedio.
      </p>

      {/* Distribución AI vs Manual */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-indigo-700">AI Auto-piloto</span>
          <span className="tabular-nums text-neutral-700">
            <span className="font-semibold">{ai}</span>
            <span className="text-neutral-400"> · {aiPct}%</span>
          </span>
        </div>
        <div className="h-2 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
          <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${aiPct}%` }} />
        </div>
        <div className="flex items-center justify-between text-sm mt-3">
          <span className="font-medium text-neutral-700">Manual (operador)</span>
          <span className="tabular-nums text-neutral-700">
            <span className="font-semibold">{manual}</span>
            <span className="text-neutral-400"> · {Math.max(0, 100 - aiPct)}%</span>
          </span>
        </div>
        <div className="h-2 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
          <div className="h-full bg-neutral-500 rounded-full" style={{ width: `${Math.max(0, 100 - aiPct)}%` }} />
        </div>
      </div>

      {/* Tiempos promedio */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="rounded-xl border border-neutral-200 p-3" data-testid="ai-stat-avg-ai">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Tiempo promedio AI</p>
          <p className="font-display font-extrabold text-2xl mt-1">{fmtMin(data.avg_minutes_ai)}</p>
          <p className="text-[11px] text-neutral-400 mt-1">{data.delivered_ai || 0} entregas</p>
        </div>
        <div className="rounded-xl border border-neutral-200 p-3" data-testid="ai-stat-avg-manual">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Tiempo promedio manual</p>
          <p className="font-display font-extrabold text-2xl mt-1">{fmtMin(data.avg_minutes_manual)}</p>
          <p className="text-[11px] text-neutral-400 mt-1">{data.delivered_manual || 0} entregas</p>
        </div>
        <div className="rounded-xl border border-neutral-200 p-3" data-testid="ai-stat-geofence">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Cerrados por geocerca</p>
          <p className="font-display font-extrabold text-2xl mt-1 text-indigo-700">{data.ai_geofence_deliveries || 0}</p>
          <p className="text-[11px] text-neutral-400 mt-1">Llegada GPS al destino</p>
        </div>
        <div className="rounded-xl border border-neutral-200 p-3" data-testid="ai-stat-no-driver">
          <p className="text-[10px] uppercase tracking-widest text-neutral-500">Sin unidades disponibles</p>
          <p className={`font-display font-extrabold text-2xl mt-1 ${(data.ai_no_driver_events || 0) > 0 ? "text-amber-600" : ""}`}>
            {data.ai_no_driver_events || 0}
          </p>
          <p className="text-[11px] text-neutral-400 mt-1">
            {data.ai_failed_events ? `${data.ai_failed_events} pipelines con error` : "Sin errores"}
          </p>
        </div>
      </div>
    </div>
  );
}
